import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, take } from 'rxjs';

import { RequestOptionsBase } from '#models/db/request/request-options-base.model';
import { environment } from 'src/environments/environment';
import { IApiRepositoryService } from './api-repository.interface';

@Injectable({
  providedIn: 'root',
})
export class ApiRepositoryService implements IApiRepositoryService {
  private apiUrl = environment.supabaseApiUrl;

  constructor(private http: HttpClient) {}

  async get<T>(requestOptions: RequestOptionsBase<T>): Promise<T> {
    const url = this.setApiUrl(requestOptions.url!);

    const { headers, params } = this.setHttpOptions(requestOptions);

    const response$ = this.http.get<T>(url, { headers, params }).pipe(take(1));

    return await firstValueFrom(response$);
  }

  async post<T, B>(requestOptions: RequestOptionsBase<T>, body: B | null): Promise<T> {
    const url = this.setApiUrl(requestOptions.url!);

    const { headers, params } = this.setHttpOptions(requestOptions);

    const response$ = this.http.post<T>(url, body, { headers, params }).pipe(take(1));

    return await firstValueFrom(response$);
  }

  async put<T, B>(requestOptions: RequestOptionsBase<T>, body: B | null): Promise<T> {
    const url = this.setApiUrl(requestOptions.url!);

    const { headers, params } = this.setHttpOptions(requestOptions);

    const response$ = this.http.put<T>(url, body, { headers, params }).pipe(take(1));

    return await firstValueFrom(response$);
  }

  async patch<T, B>(requestOptions: RequestOptionsBase<T>, body: B | null): Promise<T> {
    const url = this.setApiUrl(requestOptions.url!);

    const { headers, params } = this.setHttpOptions(requestOptions);

    const response$ = this.http.patch<T>(url, body ?? {}, { headers, params }).pipe(take(1));

    return await firstValueFrom(response$);
  }

  async delete<T>(requestOptions: RequestOptionsBase<T>): Promise<T> {
    const url = this.setApiUrl(requestOptions.url!);

    const { headers, params } = this.setHttpOptions(requestOptions);

    const response$ = this.http.delete<T>(url, { headers, params }).pipe(take(1));

    return await firstValueFrom(response$);
  }

  async request<T, B>(
    requestOptions: RequestOptionsBase<T>,
    method: 'GET' | 'POST',
    responseType: 'blob' | 'text' | 'arraybuffer' | 'json',
    body?: B
  ): Promise<T> {
    const url = this.setApiUrl(requestOptions.url!);

    const { headers, params } = this.setHttpOptions(requestOptions);

    const response$ = this.http
      .request(method, url, {
        headers,
        params,
        body,
        responseType,
      })
      .pipe(take(1));

    return await firstValueFrom(response$);
  }

  private setApiUrl(path: string | URL): string {
    let url = `${this.apiUrl}/${path}`;

    if (path instanceof URL) {
      url = path.toString();
    }

    return url;
  }

  private setHttpOptions<T>(requestOptions: RequestOptionsBase<T>) {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const params = requestOptions.params
      ? new HttpParams({
          fromObject: requestOptions.params,
        })
      : undefined;

    const options = {
      headers,
      ...(params && { params }),
    };

    return options;
  }
}
